/**
 * E002-T018: Output Tab Component
 * E002-T039: Output Mapping UI
 * S020-8.20.6: Column-Level Conflict Detection
 * S020-8.20.4: Write Permission Filtering for Target Table
 * S020-8.20.3: Multi-Output Writer (Fan-Out)
 * sl-trm: Per-Table Output Mode with Match Configuration
 *
 * Displays target table selection with per-table output mode,
 * response preview and field mapping grid.
 *
 * Flow:
 * 1. Select Target Table (Dropdown with "Same as Source" option)
 *    - S020-8.20.4: Only shows tables user has WRITE permission on
 * 2. sl-trm: Select Output Mode PER TABLE (inline dropdown)
 *    - update_source: only when target == source
 *    - append: always available
 *    - update_by_match: requires match config
 *    - upsert: requires match config
 * 3. S020-8.20.3: Multi-Output Mode for workflows returning multiple table data
 */

import { notify, Switch } from '@affine/component';
import { UserSettingsService } from '@affine/core/modules/cloud';
import { useI18n } from '@affine/i18n';
import { cssVarV2 } from '@streamlain/theme/v2';
// sl-240a: UserApiKeysModeService moved to field-mapping-grid.tsx
import { useLiveData, useService } from '@toeverything/infra';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type {
  AutomationConfig,
  MatchConfigProps,
  OutputMapping,
  OutputTable,
} from '../services/automation';
// sl-240a: MatchConfig and TargetMatchMode moved to field-mapping-grid.tsx
import type { ColumnInfo, DatabaseInfo } from '../services/database-discovery';
import { WorkflowService } from '../services/workflow';
import { TutorialRegistry } from '../tutorial/services/tutorial-registry';
import * as styles from './automation-panel.css';
import { CollapsibleSection } from './components/collapsible-section';
import { FieldMappingGrid } from './components/field-mapping-grid';
import {
  SectionAddLink,
  SectionControlRow,
  SectionRemoveButton,
  SectionTableRow,
} from './components/section-layout';
import { TableSelector } from './components/table-selector';

interface OutputTabProps {
  config: AutomationConfig;
  onUpdate: (updates: Partial<AutomationConfig>) => void;
  /** Target table columns (for mapping) - used for single table mode */
  targetColumns?: ColumnInfo[];
  /** sl-3nk: Map of tableId -> columns for multi-table field mapping */
  tableColumnsMap?: Map<string, ColumnInfo[]>;
  /** Source table columns (for resolving column IDs to names) */
  sourceColumns?: ColumnInfo[];
  /** sl-3nk: Only owner can configure AI relation matching */
  isOwner?: boolean;
}

export const OutputTab: FC<OutputTabProps> = ({
  config,
  onUpdate,
  targetColumns,
  tableColumnsMap,
  sourceColumns,
  isOwner = false,
}) => {
  const t = useI18n();
  // sl-3nk: User settings for AI relation matching toggle
  const userSettingsService = useService(UserSettingsService);
  const userSettings = useLiveData(userSettingsService.userSettings$);
  const [isUpdatingAiSetting, setIsUpdatingAiSetting] = useState(false);

  // sl-240a: userApiKeysModeService and isUsingUserKeys moved to field-mapping-grid.tsx
  // (AI keys warning is now shown in the Match Key Mode dropdown)

  // sl-mau5: Check if any mapping has Smart Lookup enabled (has matchConfigId)
  const hasSmartLookupEnabled = useMemo(() => {
    return (config.outputMappings ?? []).some(m => m.matchConfigId);
  }, [config.outputMappings]);

  // sl-3nk: Handle AI relation matching toggle
  const handleAiMatchingToggle = useCallback(
    (enabled: boolean) => {
      setIsUpdatingAiSetting(true);
      userSettingsService
        .updateUserSettings({ aiRelationMatchingEnabled: enabled })
        .catch(() => {
          notify.error({
            title: t['com.streamlain.streamlain.output.ai-matching-failed'](),
          });
        })
        .finally(() => {
          setIsUpdatingAiSetting(false);
        });
    },
    [userSettingsService, t]
  );

  const registry = useService(TutorialRegistry);
  const workflowService = useService(WorkflowService);
  const workflowSchema = useLiveData(workflowService.selectedWorkflowSchema$);

  // sl-optc: Mode is now implicit from match keys in Field Mapping Grid
  // - No match keys = append (create new rows)
  // - Match keys with notFoundBehavior='fail' = update_by_match
  // - Match keys with notFoundBehavior='create' = upsert

  const handleMappingsChange = useCallback(
    (mappings: OutputMapping[]) => {
      console.log('[OutputTab] Saving mappings:', mappings);
      onUpdate({ outputMappings: mappings });
    },
    [onUpdate]
  );

  // sl-mau5: Handlers for match configs and output tables
  const handleMatchConfigsChange = useCallback(
    (configs: MatchConfigProps[]) => {
      onUpdate({ matchConfigs: configs });
    },
    [onUpdate]
  );

  const handleOutputTablesChange = useCallback(
    (tables: OutputTable[]) => {
      onUpdate({ outputTables: tables });
    },
    [onUpdate]
  );

  // Determine which columns to show for mapping (for single table mode)
  const mappingColumns = useMemo(() => {
    return targetColumns ?? [];
  }, [targetColumns]);

  // Check if source table is selected
  const hasSourceTable = !!config.sourceTableId;

  // Build sourceTableOption for TableSelector
  const sourceTableOption = useMemo(() => {
    if (!hasSourceTable || !config.sourceTableId) return undefined;

    const fallbackName =
      config.sourceTableName ||
      t['com.streamlain.streamlain.output.source-table-fallback']();
    return {
      sourceTable: {
        blockId: config.sourceTableId,
        databaseTitle: fallbackName,
        docId: '', // Not needed for selection
        docTitle: '',
      } as DatabaseInfo,
      label: t['com.streamlain.streamlain.output.same-as-source']({
        name: fallbackName,
      }),
    };
  }, [hasSourceTable, config.sourceTableId, config.sourceTableName, t]);

  // sl-3nk: Handle adding a new output table
  const handleAddOutputTable = useCallback(() => {
    // Don't add if we already have tables without selection
    const currentTables = config.outputTables ?? [];
    if (currentTables.some(t => !t.tableId)) return;

    onUpdate({
      outputTables: [
        ...currentTables,
        { tableId: '', tableName: '', docId: '' },
      ],
    });
  }, [config.outputTables, onUpdate]);

  // sl-optc: Handle removing an output table (can remove any, but keeps at least one empty)
  const handleRemoveOutputTable = useCallback(
    (index: number) => {
      const currentTables = config.outputTables ?? [];

      // Remove the table at index
      const newTables = currentTables.filter((_, i) => i !== index);

      // If no tables left, add an empty one
      if (newTables.length === 0) {
        newTables.push({ tableId: '', tableName: '', docId: '' });
      }

      // Remove mappings for the removed table
      const removedTableId = currentTables[index]?.tableId;
      const newMappings = removedTableId
        ? (config.outputMappings ?? []).filter(
            m => m.tableId !== removedTableId
          )
        : (config.outputMappings ?? []);

      onUpdate({
        outputTables: newTables,
        outputMappings: newMappings,
      });
    },
    [config.outputTables, config.outputMappings, onUpdate]
  );

  // sl-3nk: Handle updating a specific output table
  const handleUpdateOutputTable = useCallback(
    (index: number, database: DatabaseInfo) => {
      const currentTables = config.outputTables ?? [];
      const newTables = [...currentTables];
      const current = currentTables[index];
      newTables[index] = {
        ...current,
        tableId: database.blockId,
        tableName: database.databaseTitle,
        docId: database.docId,
      };

      // Clear mappings for old table if it changed
      const oldTableId = currentTables[index]?.tableId;
      let newMappings = config.outputMappings ?? [];
      if (oldTableId && oldTableId !== database.blockId) {
        newMappings = newMappings.filter(m => m.tableId !== oldTableId);
      }

      onUpdate({
        outputTables: newTables,
        outputMappings: newMappings,
      });
    },
    [config.outputTables, config.outputMappings, onUpdate]
  );

  const arrayFieldOptions = useMemo(() => {
    const output = workflowSchema?.output ?? {};
    return Object.entries(output)
      .map(([fieldId, fieldDef]) => {
        const def = fieldDef as { name?: string; type?: string };
        return {
          id: fieldId,
          label: def.name || fieldId,
          type: def.type || 'json',
        };
      })
      .filter(field => {
        const type = field.type.toLowerCase();
        return (
          type === 'array' || type.endsWith('[]') || type.startsWith('array')
        );
      });
  }, [workflowSchema]);

  const handleUpdateOutputTableFanout = useCallback(
    (
      index: number,
      updates: {
        sourceArrayField?: string | null;
        targetAlias?: string | null;
      }
    ) => {
      const currentTables = config.outputTables ?? [];
      const newTables = [...currentTables];
      const current = newTables[index];
      if (!current) return;

      const next = { ...current };
      if (updates.sourceArrayField !== undefined) {
        if (updates.sourceArrayField) {
          next.sourceArrayField = updates.sourceArrayField;
          next.targetAlias =
            next.targetAlias ||
            next.tableName
              ?.toLowerCase()
              .replace(/[^a-z0-9]+/g, '_')
              .replace(/^_+|_+$/g, '') ||
            next.tableId;
        } else {
          delete next.sourceArrayField;
        }
      }
      if (updates.targetAlias !== undefined) {
        if (updates.targetAlias) {
          next.targetAlias = updates.targetAlias;
        } else {
          delete next.targetAlias;
        }
      }
      newTables[index] = next;
      onUpdate({ outputTables: newTables });
    },
    [config.outputTables, onUpdate]
  );

  // sl-optc: Get output tables (default to single empty entry)
  const outputTables = config.outputTables ?? [
    { tableId: '', tableName: '', docId: '' },
  ];
  const hasMultipleTables = outputTables.length > 1;

  return (
    <div className={styles.tabContent}>
      {/* sl-optc: Target Section - CollapsibleSection */}
      <div data-section="target-tables">
        <CollapsibleSection
          title={t['com.streamlain.streamlain.output.target']()}
          subtitle={t[
            'com.streamlain.streamlain.output.field-mapping-subtitle'
          ]()}
          defaultCollapsed
        >
          {/* All target tables with unified layout */}
          {outputTables.map((table, index) => {
            // Get IDs of other selected tables to exclude from dropdown
            const otherSelectedIds = outputTables
              .filter((_, i) => i !== index)
              .map(t => t.tableId)
              .filter(Boolean);

            const fanoutEnabled = !!table.sourceArrayField;
            return (
              <div key={table.tableId || `table-${index}`}>
                <SectionTableRow
                  spaced={index > 0}
                  action={
                    <SectionRemoveButton
                      onClick={() => handleRemoveOutputTable(index)}
                      title={t[
                        'com.streamlain.streamlain.output.remove-target-table'
                      ]()}
                    />
                  }
                >
                  <TableSelector
                    value={
                      table.tableId ||
                      (index === 0 ? config.sourceTableId : undefined)
                    }
                    onChange={db => handleUpdateOutputTable(index, db)}
                    placeholder={t[
                      'com.streamlain.streamlain.output.select-target-table'
                    ]()}
                    excludeBlockIds={otherSelectedIds}
                    sourceTableOption={
                      index === 0 ? sourceTableOption : undefined
                    }
                    requiredPermission="write"
                  />
                </SectionTableRow>
                {table.tableId && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: fanoutEnabled
                        ? 'auto minmax(0, 1fr) minmax(74px, 110px)'
                        : 'auto minmax(74px, 110px)',
                      gap: '8px',
                      alignItems: 'center',
                      marginTop: '6px',
                      padding: '6px 0 0',
                    }}
                  >
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        color: 'var(--streamlain-text-secondary-color)',
                      }}
                      title={t[
                        'com.streamlain.streamlain.output.fanout-title'
                      ]()}
                    >
                      <Switch
                        checked={fanoutEnabled}
                        onChange={checked =>
                          handleUpdateOutputTableFanout(index, {
                            sourceArrayField: checked
                              ? table.sourceArrayField ||
                                arrayFieldOptions[0]?.id ||
                                ''
                              : null,
                          })
                        }
                      />
                      <span>
                        {t['com.streamlain.streamlain.output.fanout']()}
                      </span>
                    </label>
                    {fanoutEnabled && (
                      <select
                        value={table.sourceArrayField ?? ''}
                        onChange={event =>
                          handleUpdateOutputTableFanout(index, {
                            sourceArrayField: event.target.value,
                          })
                        }
                        title={t[
                          'com.streamlain.streamlain.output.fanout-array-field'
                        ]()}
                        style={{
                          width: '100%',
                          minWidth: 0,
                          height: '28px',
                          borderRadius: '6px',
                          border: '1px solid var(--streamlain-border-color)',
                          background:
                            'var(--streamlain-background-secondary-color)',
                          color: 'var(--streamlain-text-primary-color)',
                          fontSize: '12px',
                          padding: '0 8px',
                        }}
                      >
                        <option value="">
                          {t[
                            'com.streamlain.streamlain.output.fanout-array-field'
                          ]()}
                        </option>
                        {arrayFieldOptions.map(field => (
                          <option key={field.id} value={field.id}>
                            {field.label}
                          </option>
                        ))}
                      </select>
                    )}
                    <input
                      value={table.targetAlias ?? ''}
                      onChange={event =>
                        handleUpdateOutputTableFanout(index, {
                          targetAlias: event.target.value,
                        })
                      }
                      placeholder={t[
                        'com.streamlain.streamlain.output.fanout-alias'
                      ]()}
                      title={t[
                        'com.streamlain.streamlain.output.fanout-alias-title'
                      ]()}
                      style={{
                        width: '100%',
                        minWidth: 0,
                        height: '28px',
                        borderRadius: '6px',
                        border: '1px solid var(--streamlain-border-color)',
                        background:
                          'var(--streamlain-background-secondary-color)',
                        color: 'var(--streamlain-text-primary-color)',
                        fontSize: '12px',
                        padding: '0 8px',
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}

          {/* Add Table Button */}
          <SectionAddLink onClick={handleAddOutputTable}>
            <span>+</span>
            {t['com.streamlain.streamlain.common.add-table']()}
          </SectionAddLink>
        </CollapsibleSection>
      </div>

      {/* sl-3nk: Field Mapping - grouped by table for multi-output */}
      <div data-section="field-mapping">
        {!hasMultipleTables ? (
          // Single table: simple field mapping grid
          <CollapsibleSection
            title={t['com.streamlain.streamlain.output.field-mapping']()}
            subtitle={t[
              'com.streamlain.streamlain.output.field-mapping-subtitle'
            ]()}
            onInfoClick={() => registry.start('automation:outputMapping')}
            defaultCollapsed
          >
            <FieldMappingGrid
              tableId={outputTables[0]?.tableId}
              sourceTableId={config.sourceTableId ?? undefined}
              targetColumns={mappingColumns}
              mappings={config.outputMappings ?? []}
              onMappingsChange={handleMappingsChange}
              matchConfigs={config.matchConfigs ?? []}
              onMatchConfigsChange={handleMatchConfigsChange}
              outputTables={config.outputTables ?? []}
              onOutputTablesChange={handleOutputTablesChange}
              sourceColumns={sourceColumns}
              noWrapper
            />
          </CollapsibleSection>
        ) : (
          // Multiple tables: grouped field mapping
          <CollapsibleSection
            title={t['com.streamlain.streamlain.output.field-mapping']()}
            subtitle={t[
              'com.streamlain.streamlain.output.field-mapping-multi-subtitle'
            ]()}
            onInfoClick={() => registry.start('automation:outputMapping')}
            defaultCollapsed
          >
            {outputTables
              .filter(table => table.tableId) // Only show tables with selection
              .map((table, index) => {
                const tableColumns = tableColumnsMap?.get(table.tableId) ?? [];
                const tableMappings = (config.outputMappings ?? []).filter(
                  m => m.tableId === table.tableId
                );
                const mappedCount = tableMappings.length;

                return (
                  <div
                    key={table.tableId}
                    style={{
                      marginBottom:
                        index < outputTables.length - 1 ? '16px' : 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: 'var(--streamlain-text-secondary-color)',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <span>
                        {table.tableName ||
                          t['com.streamlain.streamlain.common.untitled']()}
                      </span>
                      {mappedCount > 0 && (
                        <span
                          style={{
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '10px',
                            backgroundColor: 'var(--streamlain-primary-color)',
                            color: 'white',
                          }}
                        >
                          {mappedCount}
                        </span>
                      )}
                    </div>
                    <FieldMappingGrid
                      tableId={table.tableId}
                      sourceTableId={config.sourceTableId ?? undefined}
                      targetColumns={tableColumns}
                      mappings={tableMappings}
                      onMappingsChange={newMappings => {
                        // Replace mappings for this table, keep others
                        const otherMappings = (
                          config.outputMappings ?? []
                        ).filter(m => m.tableId !== table.tableId);
                        handleMappingsChange([
                          ...otherMappings,
                          ...newMappings,
                        ]);
                      }}
                      matchConfigs={config.matchConfigs ?? []}
                      onMatchConfigsChange={handleMatchConfigsChange}
                      outputTables={config.outputTables ?? []}
                      onOutputTablesChange={handleOutputTablesChange}
                      sourceColumns={sourceColumns}
                      noWrapper // Skip inner CollapsibleSection - we're already in one
                    />
                  </div>
                );
              })}
          </CollapsibleSection>
        )}
      </div>

      {/* sl-3nk: AI Relation Matching Toggle - only for owner when Smart Lookup is used */}
      {isOwner && hasSmartLookupEnabled && (
        <SectionControlRow
          label={t['com.streamlain.streamlain.output.ai-matching']()}
          description={t[
            'com.streamlain.streamlain.output.ai-matching-subtitle'
          ]()}
        >
          {isUpdatingAiSetting ? (
            <div
              style={{
                width: '20px',
                height: '20px',
                border: `2px solid ${cssVarV2('layer/insideBorder/border')}`,
                borderTopColor: 'var(--streamlain-primary-color)',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
          ) : (
            <Switch
              checked={userSettings?.aiRelationMatchingEnabled ?? false}
              onChange={handleAiMatchingToggle}
            />
          )}
        </SectionControlRow>
      )}
    </div>
  );
};
